import * as ErrorHandlingHelper from "@hireroo/app-helper/error-handling";
import { Company } from "@hireroo/app-store/essential/employee";
import { TagField } from "@hireroo/app-store/widget/e/TagField";
import { Snackbar } from "@hireroo/app-store/widget/shared/Snackbar";
import * as Graphql from "@hireroo/graphql/client/urql";
import { useTranslation } from "@hireroo/i18n";
import { Widget } from "@hireroo/presentation";
import * as Sentry from "@sentry/react";
import { ErrorBoundary, withErrorBoundary } from "@sentry/react";
import React from "react";

import TagFieldContainer, { TagFieldContainerProps } from "./TagFieldContainer";

export type RemoteTagFieldFetchContainerProps = Omit<
  TagFieldContainerProps,
  "refresh" | "loading" | "deleteTag" | "disableDeleteButton" | "deleteDialogProps"
>;
const FETCH_LIMIT = 100;

const RemoteTagFieldFetchContainer: React.FC<RemoteTagFieldFetchContainerProps> = props => {
  const { t } = useTranslation();
  const companyId = Company.useStrictActiveCompanyId();
  const [result, refresh] = Graphql.useListRemoteTagsForRemoteTagFieldQuery({
    variables: {
      companyId: companyId,
      size: FETCH_LIMIT,
      // TODO: please change when create pagination.
      cursorSeconds: null,
      withCount: false,
    },
    requestPolicy: "cache-and-network",
  });

  const [deleteRemoteTagResult, deleteRemoteTag] = Graphql.useDeleteRemoteTagMutation();

  const deleteTag = React.useCallback(
    (tag: TagField.TagItem) => {
      deleteRemoteTag({
        input: {
          companyId: tag.companyId,
          tagName: tag.name,
        },
      }).then(({ data, error }) => {
        if (data) {
          TagField.deleteTag(tag);
          refresh();
          Snackbar.notify({
            severity: "success",
            message: t("タグを削除しました。"),
          });
        }

        if (error) {
          Sentry.captureException(error);
          const errorNotification = ErrorHandlingHelper.generateErrorNotification(error, t("タグの削除に失敗しました。"));
          Snackbar.notify({
            severity: "error",
            message: errorNotification.message,
          });
        }
      });
    },
    [deleteRemoteTag, refresh, t],
  );

  React.useEffect(() => {
    if (result.data) {
      if (result.data?.remoteTagsByCompanyId?.tags) {
        TagField.updateTags(result.data.remoteTagsByCompanyId);
      }
    }
  }, [result.data]);

  React.useEffect(() => {
    return () => {
      TagField.clear();
    };
  }, []);

  if (result.error) {
    return <Widget.RefreshPanel refresh={refresh} />;
  }

  const containerProps: TagFieldContainerProps = {
    name: props.name,
    variant: props.variant,
    refresh: refresh,
    loading: result.fetching,
    deleteTag: deleteTag,
    disableDeleteButton: deleteRemoteTagResult.fetching,
    deleteDialogProps: {
      items: [
        {
          name: t("一度削除すると元に戻すことはできません。"),
        },
        {
          name: t("全てのインタビューからもタグが削除されます。"),
        },
      ],
    },
  };
  return (
    <ErrorBoundary>
      <TagFieldContainer {...containerProps} />
    </ErrorBoundary>
  );
};

RemoteTagFieldFetchContainer.displayName = "RemoteTagFieldFetchContainer";

export default withErrorBoundary(RemoteTagFieldFetchContainer, {});
